<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import html2canvas from "html2canvas";
  import DownloadButton from "$components/Download/DownloadButton.svelte";
  import { logClickToGA } from "@urbaninstitute/dataviz-components";
  import { tick } from "svelte";

  /** @typedef {import("svelte").Snippet<[() => void]>} ReadySnippet */

  /**
   * @property {number} id id of the chart (used for export)
   * @property {string} filename
   * @property {ReadySnippet} children
   * @property {string} label
   */
  let { id, filename, children, label } = $props();

  // setup export flag and ref
  let exportFlag = $state(false);

  /**
   * Callback passed to children so they can signal when they are fully rendered
   * and ready to be captured by html2canvas.
   * @type {() => void}
   */
  let onreadyCallback = $state(() => {});

  /** @type {HTMLElement} */
  let exportRef;

  // handle export flag and download
  export const handleExport = async () => {
    // Create a deferred promise that the child resolves by calling onready()
    /** @type {() => void} */
    let resolveReady = () => {};
    const readyPromise = new Promise((resolve) => {
      resolveReady = /** @type {() => void} */ (() => resolve(undefined));
    });

    // Safety timeout — prevents a silent hang if a child forgets to call onready
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 15000));

    // Store the resolver so the template can pass it to children as onreadyCallback
    onreadyCallback = /** @type {() => void} */ (resolveReady);

    // Show the offscreen export container, which renders children with onreadyCallback
    exportFlag = true;

    // Wait for Svelte to flush DOM updates
    await tick();

    // Wait for the child to signal readiness (or fall back to the safety timeout)
    await Promise.race([readyPromise, timeoutPromise]);

    try {
      const canvas = await html2canvas(exportRef, {
        // filter out elements that should not be included in the image
        ignoreElements: (el) => {
          if (el.classList.contains("button-container")) return true;
          if (el.classList.contains("download-image-container") && el !== exportRef) return true;
          if (el.classList.contains("maplibregl-map") && !exportRef.contains(el)) return true;
          return false;
        }
      });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png").replace("image/jpeg", "image/octet-stream");
      a.download = `${filename}.png`;
      a.click();
      exportFlag = false;
      return { success: true };
    } catch {
      exportFlag = false;
      return { success: false };
    }
  };

  /**
   * @param {MouseEvent} e
   */
  function handleButtonClick(e) {
    handleExport();
    logClickToGA(/** @type {HTMLElement} */ (e.currentTarget), "download-button-click");
  }
</script>

<DownloadButton {label} onclick={handleButtonClick} isLoading={exportFlag} />
<!-- exported chart container -->
<div id={`exported-chart-${id}`} class="download-image-container" bind:this={exportRef}>
  {#if exportFlag}
    {@render children?.(onreadyCallback)}
  {/if}
</div>

<style>
  /* OFFSCREEN CHART POSITIONED ABSOLUTELY */
  .download-image-container {
    padding: var(--spacing-6) var(--spacing-12);
    position: absolute;
    left: -50000px;
    width: 800px;
  }
</style>
